import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import ConnectionOverviewScreen from './ConnectionDetailsPart';
import ConnectionsSidebar from './ConnectionsSidebar';

import { PersonalConnection } from '../../../../types/misc';
import { connectionService } from '../../../../services';

export default function ConnectionsListPage() {
  const { t } = useTranslation();
  const [connections, setConnections] = useState<PersonalConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<PersonalConnection | null>(null);

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState<boolean>(false);

  const [params, setSearchParams] = useSearchParams();

  useEffect(() => {
    const mql: MediaQueryList = window.matchMedia('(max-width: 640px)');

    const onChangeModern = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    type LegacyMql = MediaQueryList & {
      addListener: (listener: (mql: MediaQueryList) => void) => void;
      removeListener: (listener: (mql: MediaQueryList) => void) => void;
    };
    const onChangeLegacy = (m: MediaQueryList) => setIsMobile(m.matches);

    setIsMobile(mql.matches);

    if ('addEventListener' in mql && typeof (mql as unknown as EventTarget).addEventListener === 'function') {
      (mql as unknown as EventTarget).addEventListener('change', onChangeModern as EventListener);
      return () => (mql as unknown as EventTarget).removeEventListener('change', onChangeModern as EventListener);
    }

    const legacy = mql as LegacyMql;
    legacy.addListener(onChangeLegacy);
    return () => legacy.removeListener(onChangeLegacy);
  }, []);

  // Load all connections on mount
  useEffect(() => {
    let mounted = true;

    const loadConnections = async () => {
      try {
        const conns = await connectionService.getMyConnections();
        const sorted = conns.sort((a, b) => a.firstName.localeCompare(b.firstName));
        if (!mounted) return;
        setConnections(sorted);
        if (!isMobile && sorted.length > 0) {
          setSelectedConnection(sorted[0]);
        }
      } catch (err) {
        console.error('Failed to load connections', err);
        if (mounted) {
          setConnections([]);
          setSelectedConnection(null);
        }
      }
    };

    loadConnections();

    return () => {
      mounted = false;
    };
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile && connections.length > 0 && !selectedConnection) {
      setSelectedConnection(connections[0]);
    }

    if (isMobile) {
      setMobileDetailsOpen(false);
    }
  }, [isMobile, connections, selectedConnection]);

  // Sync selected connection with route search param: ?connection=123
  useEffect(() => {
    const param = params.get('connection');
    if (param) {
      const id = Number(param);
      const found = connections.find((c) => c.id === id) || null;
      setSelectedConnection(found);
      if (isMobile) {
        setMobileDetailsOpen(!!found);
      }
      return;
    }

    // no param
    if (!isMobile) {
      setSelectedConnection((prev) => prev ?? connections[0] ?? null);
    } else {
      setMobileDetailsOpen(false);
      setSelectedConnection(null);
    }
  }, [params, connections, isMobile]);

  const handleConnectionDeleted = (id: number) => {
    setConnections((prev) => {
      const next = prev.filter((c) => c.id !== id);

      if (selectedConnection?.id === id) {
        const nextSelected = next.length > 0 ? next[0] : null;
        setSelectedConnection(nextSelected);
        // update route param accordingly
        if (nextSelected) {
          setSearchParams({ connection: nextSelected.id.toString() });
        } else {
          setSearchParams({});
        }
      }

      return next;
    });

    if (isMobile) {
      setMobileDetailsOpen(false);
    }
  };

  const handleSelectConnection = (connection: PersonalConnection) => {
    setSelectedConnection(connection);
    setSearchParams({ connection: connection.id.toString() });
    if (isMobile) setMobileDetailsOpen(true);
  };

  if (!isMobile) {
    return (
      <div className="flex h-full">
        <ConnectionsSidebar
          connections={connections}
          selectedConnectionId={selectedConnection?.id}
          onSelectConnection={handleSelectConnection}
        />
        {!selectedConnection ? (
          <div className="flex flex-1 items-center justify-center bg-white">
            <p className="text-secondary-400">{t('overview.connection.noSelection')}</p>
          </div>
        ) : (
          <ConnectionOverviewScreen connection={selectedConnection} onDeleted={handleConnectionDeleted} />
        )}
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-secondary">
      {!mobileDetailsOpen ? (
        <ConnectionsSidebar
          connections={connections}
          selectedConnectionId={selectedConnection?.id}
          onSelectConnection={handleSelectConnection}
          isMobile
        />
      ) : selectedConnection ? (
        <div className="flex h-full w-full flex-col">
          <div className="border-b bg-secondary p-4">
            <button
              type="button"
              onClick={() => {
                setMobileDetailsOpen(false);
                // clear selection param when closing details on mobile
                setSearchParams({});
              }}
              className="flex items-center gap-3 text-primary">
              <FiArrowLeft className="h-5 w-5" />
              <span className="text-sm">{t('general.back')}</span>
            </button>
          </div>
          <div className="flex-1 overflow-auto">
            <ConnectionOverviewScreen connection={selectedConnection} onDeleted={handleConnectionDeleted} />
          </div>
        </div>
      ) : (
        <div className="flex h-full items-center justify-center">
          <p className="text-secondary-400">{t('overview.connection.noSelection')}</p>
        </div>
      )}
    </div>
  );
}
